/**
 * V3 Socket.IO client hook — Sprint 3.7 (live anomaly updates).
 *
 * Wraps a single shared `socket.io-client` connection so multiple components
 * can subscribe to backend events without each creating their own socket.
 *
 *   import { useV3Socket, useSocketEvent } from '../hooks/useV3Socket';
 *
 *   const { connected } = useV3Socket();         // bootstrap + status
 *   useSocketEvent('anomaly.detected', (p) => …) // bind a handler
 *
 * Backend events (defined in API-EGoalV3/src/realtime/notificationEmitter.js):
 *   - anomaly.detected
 *   - insight.generated
 *   - brief.ready
 *
 * The socket auto-joins room `concessionaire:<id>` based on the JWT, so
 * callers don't need to subscribe explicitly.
 */
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import config from '../../../config';

function getSocketBaseURL() {
    const host = config.hostname || '';
    return host.replace(/\/api\/?$/, '');
}

let socketInstance = null;
let activeToken = null;
// Consecutive "unauthorized" reconnects handled by a token refresh. Reset on a
// successful connect. Caps the refresh→reconnect loop so a persistently-rejected
// token ends the session instead of spinning forever.
let unauthorizedRefreshTries = 0;
const MAX_UNAUTHORIZED_REFRESH_TRIES = 2;

function getOrCreateSocket() {
    if (typeof window === 'undefined') return null;
    const token = window.localStorage ? window.localStorage.getItem('sessiontoken') : null;
    if (!token) {
        if (socketInstance) {
            try { socketInstance.disconnect(); } catch (_) { /* noop */ }
            socketInstance = null;
            activeToken = null;
        }
        return null;
    }

    if (socketInstance && activeToken !== token) {
        try { socketInstance.disconnect(); } catch (_) { /* noop */ }
        socketInstance = null;
    }
    if (socketInstance) return socketInstance;

    activeToken = token;
    socketInstance = io(getSocketBaseURL(), {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        withCredentials: true,
    });

    // A successful connect means the current token is good — clear the retry counter.
    socketInstance.on('connect', () => { unauthorizedRefreshTries = 0; });

    // The backend's Socket.IO auth middleware rejects bad/expired tokens with an
    // Error whose message starts with "unauthorized". Mirror the REST interceptor:
    // the access token has likely just expired, so try a silent refresh and
    // reconnect with the fresh token before ending the session. Only when the
    // refresh genuinely fails (or keeps being rejected) do we expire — so an
    // ACTIVE user is never kicked over a transient token gap. Idle logout is owned
    // by SessionGuard's deterministic timer, not this handler.
    socketInstance.on('connect_error', (err) => {
        const msg = (err && err.message) ? String(err.message).toLowerCase() : '';
        if (!msg.includes('unauthorized')) return;

        const expire = () => {
            try {
                const { handleSessionExpired } = require('./sessionExpired');
                if (typeof handleSessionExpired === 'function') handleSessionExpired();
            } catch (_) { /* noop */ }
        };

        if (unauthorizedRefreshTries >= MAX_UNAUTHORIZED_REFRESH_TRIES) {
            expire();
            return;
        }
        unauthorizedRefreshTries += 1;
        try {
            const { refreshAccessToken } = require('./tokenRefresh');
            Promise.resolve(refreshAccessToken()).then((newToken) => {
                if (newToken && socketInstance) {
                    activeToken = newToken;
                    try {
                        socketInstance.auth = { token: newToken };
                        socketInstance.connect();
                    } catch (_) { /* noop */ }
                } else {
                    expire();
                }
            });
        } catch (_) {
            expire();
        }
    });

    return socketInstance;
}

/**
 * Push a freshly-refreshed access token into the live socket so it stops
 * reconnecting with the old (soon-expired) one. Called by SessionGuard after a
 * proactive refresh. No-op if the socket isn't up or the token is unchanged.
 */
export function updateSocketToken(newToken) {
    if (!newToken || !socketInstance) return;
    if (activeToken === newToken) return;
    activeToken = newToken;
    try {
        socketInstance.auth = { token: newToken };
        if (socketInstance.connected) socketInstance.disconnect();
        socketInstance.connect();
    } catch (_) { /* noop */ }
}

/**
 * Tear down the singleton socket. Called from sessionExpired so the client
 * stops reconnecting with a dead JWT after logout/expiry.
 */
export function disconnectSocket() {
    if (!socketInstance) return;
    try { socketInstance.disconnect(); } catch (_) { /* noop */ }
    socketInstance = null;
    activeToken = null;
}

/**
 * Bootstraps (or reuses) the singleton socket and tracks connection status.
 * Returns { socket, connected }.
 */
export function useV3Socket() {
    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        const s = getOrCreateSocket();
        if (!s) return undefined;
        socketRef.current = s;
        setConnected(!!s.connected);

        const onConnect = () => setConnected(true);
        const onDisconnect = () => setConnected(false);
        const onError = () => setConnected(false);

        s.on('connect', onConnect);
        s.on('disconnect', onDisconnect);
        s.on('connect_error', onError);

        return () => {
            s.off('connect', onConnect);
            s.off('disconnect', onDisconnect);
            s.off('connect_error', onError);
        };
    }, []);

    return { socket: socketRef.current, connected };
}

/**
 * Subscribe to a Socket.IO event for the lifetime of the calling component.
 * The handler is held in a ref so callers don't have to memoize it.
 */
export function useSocketEvent(eventName, handler) {
    const { socket } = useV3Socket();
    const handlerRef = useRef(handler);

    useEffect(() => { handlerRef.current = handler; }, [handler]);

    useEffect(() => {
        if (!socket || !eventName) return undefined;
        const wrapper = (...args) => {
            const fn = handlerRef.current;
            if (typeof fn === 'function') fn(...args);
        };
        socket.on(eventName, wrapper);
        return () => { socket.off(eventName, wrapper); };
    }, [socket, eventName]);
}

export default useV3Socket;
